import React, { useMemo, useState, useEffect } from 'react'
import { Dropdown } from 'react-bootstrap'
import log from 'loglevel'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { DOCUMENT_TYPE } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import {
  RECEPTOR_TYPE,
  ReceptorRecord,
} from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { SourceFilterValues } from 'src/components/Reusable/IndexPage/FilterBar/FilterDropdowns/SourceFilterDropdown'
import { useLocation, useParams, useNavigate, Link } from 'react-router-dom'
import queryString, { ParsedQuery } from 'query-string'
import {
  GridColumn,
  GridContainer,
  GridRow,
} from 'src/components/Reusable/Grid'
import { StickyGridHeader } from 'src/components/Reusable/Grid/Grid.styles'
import { ReceptorLogo } from 'src/components/async/model/receptor/ReceptorLogo'
import { FlexRow, FlexAlign } from 'src/components/Reusable/Flex'
import { DropdownToggleButton } from 'src/components/Reusable/Buttons'
// import { ExternalLink } from 'src/components/Reusable/Text/Link'
import {
  BasicDropdownMenuItem,
  BasicDropdownMenu,
} from 'src/components/Reusable/BasicDropdown/BasicDropdown.styles'
import { ReactComponent as VerticalEllipsesIcon } from 'src/components/Icons/assets/vertical-ellipses-icon.svg'
import PolicyIconPath from 'src/components/Icons/NavIcons/icons-policies.svg'
import EvidenceIconPath from 'src/components/Icons/NavIcons/icons-evidences.svg'
import { Spinner } from 'src/Throbber/Spinner'
import { E2E_IDS } from 'src/Utils/testIds'
import { APP_ANALYTICS_EVENTS } from 'src/analytics/analytics.constants'
import { useAccountReceptors } from 'src/components/async/model/receptor/useReceptors'
import { ReceptorsAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { TASK_STATUS } from '@trustero/trustero-api-web/lib/task/task_pb'
import { useSchedules } from 'src/pages/Scheduling/SchedulingPage.hooks'
import { formatISODateTime } from 'src/Utils/formatDate'
import { formatScheduleType } from 'src/pages/Scheduling/SchedulingHelpers'
import { useConfirmationModal } from 'src/components/ModalForms/useConfirmationModal'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'
import { useApplySearch } from 'src/Utils/search/search.hooks'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { getReceptorTemplate } from 'src/xgenerated/receptor'
import {
  usePaginateItemsWithNavigate,
  usePageSizeFromUrl,
} from 'src/components/Reusable/paginate/paginate.hooks'
import { PaginateButtonsGroup } from 'src/components/Reusable/paginate/PaginateButtons'
import { GetEvidenceIndexPageRequest } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { linkWithSearch } from 'src/components/PageLayout/Permalink'
import { PlainTooltip } from 'src/components/Reusable/Tooltip/PlainTooltip/PlainTooltip'
import { TooltipPositions } from 'src/components/Reusable/Tooltip/Tooltip'
import { InformationalEmptyState } from 'src/components/PageLayout/InformationalEmptyState'
import { NoFilterResults } from 'src/components/PageLayout/NoFilterResults'
import { useHasNewPolicyPage } from 'src/context/Gatekeeper'
import {
  ReceptorStatusLabels,
  RECEPTORS_PAGE_SIZE,
} from '../receptors.constants'
import {
  useDeleteReceptor,
  useScanReceptor,
  useScanDirectorySetting,
  useInProgressReceptorScans,
  useActivateReceptor,
} from '../receptors.hooks'
import {
  useListDirectoryScanSettings,
  useDeleteDirectoryScanSetting,
  useEvidenceIndexPage,
} from '../../Evidence/evidence.hooks'
import { StyledEvidenceGrid } from '../../Evidence/grid/EvidenceGrid.styles'
import { ErrorIcon, SuccessIcon } from './DocumentScanGrid.styles'

export interface DocumentScanGridData {
  id: string
  name: string
  type: string
  receptor: string
  isEnabled: boolean
  connectedStatus:
    | 'Connected'
    | 'Running'
    | 'Error'
    | 'Missing Credentials'
    | 'Invalid Credentials'
    | 'Disabled'
  exceptionMessage?: string
  servicesCount: number
  schedule: string
  lastScan: string
  lastScanStatus: 'success' | 'error' | 'pending'
  visibilities: string[]
  scheduleId?: string
  receptorId: string
  evidenceCount?: number
  isFolderScan: boolean
  documentType?: DOCUMENT_TYPE
}

const normalizeBracketedQueryKey = (key: string): string =>
  key.replace(/(\[\])+$/g, '')

const normalizeParsedQuery = (
  query: ParsedQuery<string>,
): ParsedQuery<string> => {
  const normalized: ParsedQuery<string> = {}
  const toArray = (value: string | string[] | null | undefined): string[] => {
    if (value === undefined || value === null) return []
    if (Array.isArray(value)) return value
    return [value]
  }

  Object.entries(query).forEach(([rawKey, rawValue]) => {
    const key = normalizeBracketedQueryKey(rawKey)
    const mergedValues = [...toArray(normalized[key]), ...toArray(rawValue)]
    const deduped = Array.from(new Set(mergedValues))
    if (deduped.length === 0) return
    normalized[key] = deduped.length === 1 ? deduped[0] : deduped
  })

  return normalized
}

const getReceptorScheduleDetails = (receptor?: ReceptorRecord) => {
  const schedule = receptor?.getSchedule()
  const scheduleTypes = schedule?.getScheduletypesList() || []
  const receptorLastRunMillis = receptor?.getLastrun() || 0
  const scheduleLastRunAtSeconds = schedule?.getLastrunat() || 0
  const hasLastRun = scheduleLastRunAtSeconds > 0 || receptorLastRunMillis > 0
  const runTaskStatusStr =
    schedule?.getTaskstatus() ||
    (hasLastRun
      ? TASK_STATUS.COMPLETED.toString()
      : TASK_STATUS.NOT_STARTED.toString())
  const runTaskStatus = parseInt(runTaskStatusStr) as TASK_STATUS
  const scheduleName = schedule?.getSchedulename?.() || ''
  let gridStatus: 'success' | 'error' | 'pending' = 'pending'

  switch (runTaskStatus) {
    case TASK_STATUS.COMPLETED:
      gridStatus = 'success'
      break
    case TASK_STATUS.STOPPED:
    case TASK_STATUS.MARKED:
      gridStatus = 'error'
      break
    default:
      gridStatus = 'pending'
      break
  }

  const lastScan =
    scheduleLastRunAtSeconds > 0
      ? formatISODateTime(new Timestamp().setSeconds(scheduleLastRunAtSeconds))
      : receptorLastRunMillis > 0
      ? formatISODateTime(new Date(receptorLastRunMillis))
      : ''

  return {
    schedule:
      scheduleName || (schedule ? formatScheduleType(scheduleTypes) : ''),
    lastScan,
    lastScanStatus: gridStatus,
    scheduleId: schedule?.getScheduleid(),
  }
}

const getReceptorTypeLabel = (type: number): string => {
  switch (type) {
    case RECEPTOR_TYPE.RECEPTOR:
      return 'Receptor'
    case RECEPTOR_TYPE.COLLABORATION_SYSTEM:
      return 'Document Management'
    case RECEPTOR_TYPE.GRC_PLATFORM:
      return 'GRC Platform'
    case RECEPTOR_TYPE.WORKFLOW_PLATFORM:
      return 'Workflow Management'
    default:
      return 'Receptor'
  }
}

export const DocumentScanGrid = ({
  documentTypeFilter,
  showReceptors = true,
  policyModelId,
  receptorTypeFilter,
}: {
  documentTypeFilter?: DOCUMENT_TYPE
  showReceptors?: boolean
  policyModelId?: string
  receptorTypeFilter?: RECEPTOR_TYPE
} = {}): JSX.Element => {
  const hasNewPolicyPage = useHasNewPolicyPage()
  const templateColumns = hasNewPolicyPage
    ? '2fr 1.5fr 1.5fr 1.5fr 1fr 1fr 1.5fr 1.5fr auto'
    : '2fr 1.5fr 1.5fr 1.5fr 1fr 1.5fr 1.5fr auto'
  const { data: receptors } = useAccountReceptors()
  const { data: schedulesResponse } = useSchedules()
  const { data: directoryScanSettings } = useListDirectoryScanSettings(
    undefined,
    policyModelId,
  )
  const [isPolling, setIsPolling] = useState(false)
  const { data: inProgressScans } = useInProgressReceptorScans(true, {
    refreshInterval: isPolling ? 5000 : 0,
  })
  const location = useLocation()
  const navigate = useNavigate()
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
  const normalizedQueryParams = useMemo(
    () =>
      normalizeParsedQuery(
        queryString.parse(location.search, {
          arrayFormat: 'bracket',
        }),
      ),
    [location.search],
  )

  useEffect(() => {
    const normalizedSearch = queryString.stringify(normalizedQueryParams, {
      arrayFormat: 'bracket',
    })
    const currentSearch = location.search.startsWith('?')
      ? location.search.slice(1)
      : location.search

    if (normalizedSearch !== currentSearch) {
      navigate(
        {
          pathname: location.pathname,
          search: normalizedSearch ? `?${normalizedSearch}` : '',
        },
        { replace: true },
      )
    }
  }, [navigate, location.pathname, location.search, normalizedQueryParams])

  const schedulesMap = useMemo(() => {
    const map = new Map<string, string>()
    schedulesResponse?.getSchedulesList().forEach((s) => {
      map.set(s.getId(), s.getName())
    })
    return map
  }, [schedulesResponse])

  const receptorsData = useMemo(() => {
    const queryParams: ParsedQuery<string> = normalizedQueryParams
    const ensureArray = (
      value: string | string[] | null | undefined,
    ): string[] => {
      if (!value) return []
      if (Array.isArray(value)) return value
      return [value]
    }

    const sources = ensureArray(queryParams[FilterParam.SOURCE])
    const receptorIds = ensureArray(queryParams[FilterParam.RECEPTOR])
    const grcIds = ensureArray(queryParams[FilterParam.GRC_PLATFORM])
    const docIds = ensureArray(
      queryParams[FilterParam.DOCUMENT_MANAGEMENT_SYSTEM],
    )
    const workflowIds = ensureArray(
      queryParams[FilterParam.WORKFLOW_MANAGEMENT_SYSTEM],
    )
    const folderScanIds = ensureArray(queryParams[FilterParam.FOLDER_SCAN])
    const statusFilters = ensureArray(queryParams[FilterParam.STATUS])
    const statusFilterSet = new Set(statusFilters)
    const hasStatusFilter = statusFilterSet.size > 0
    const includeActive = hasStatusFilter
      ? statusFilterSet.has(ReceptorStatusLabels.ACTIVE)
      : true
    const includeDeactivated = hasStatusFilter
      ? statusFilterSet.has(ReceptorStatusLabels.DEACTIVATED)
      : true

    const matchesStatusFilter = (isEnabled: boolean): boolean => {
      if (includeActive && includeDeactivated) return true
      if (includeActive) return isEnabled
      if (includeDeactivated) return !isEnabled
      return false
    }

    const isGroupSelected = (group: SourceFilterValues | 'folder_scan') =>
      sources.includes(group)

    const hasAnyFilter =
      sources.length > 0 ||
      receptorIds.length > 0 ||
      grcIds.length > 0 ||
      docIds.length > 0 ||
      workflowIds.length > 0 ||
      folderScanIds.length > 0

    const filteredReceptors = !showReceptors
      ? []
      : receptors?.getReceptorsList().filter((r) => {
          if (!matchesStatusFilter(r.getIsenabled())) return false
          const receptorTemplate = getReceptorTemplate(r.getModelid())
          if (receptorTemplate.type === RECEPTOR_TYPE.GRC_PLATFORM) return false
          if (
            receptorTypeFilter !== undefined &&
            receptorTemplate.type !== receptorTypeFilter
          )
            return false

          if (!hasAnyFilter) return true

          const modelId = r.getModelid()

          // Check if specific ID is selected
          const isSpecificMatch =
            receptorIds.includes(modelId) ||
            grcIds.includes(modelId) ||
            docIds.includes(modelId) ||
            workflowIds.includes(modelId)

          if (isSpecificMatch) return true

          // Check if group is selected
          switch (receptorTemplate.type) {
            case RECEPTOR_TYPE.RECEPTOR:
              return isGroupSelected(SourceFilterValues.RECEPTOR)
            case RECEPTOR_TYPE.COLLABORATION_SYSTEM:
              return isGroupSelected(
                SourceFilterValues.DOCUMENT_MANAGEMENT_SYSTEM,
              )
            case RECEPTOR_TYPE.WORKFLOW_PLATFORM:
              return isGroupSelected(
                SourceFilterValues.WORKFLOW_MANAGEMENT_SYSTEM,
              )
            default:
              return false
          }
        }) || []

    const mappedReceptors = filteredReceptors.map((r): DocumentScanGridData => {
      const receptorTemplate = getReceptorTemplate(r.getModelid())
      const { schedule, lastScan, lastScanStatus, scheduleId } =
        getReceptorScheduleDetails(r)
      const receptorDisplayName = (receptorTemplate.name || '').trim()
      const receptorName = (r.getName() || receptorDisplayName || '').trim()
      const receptorTypeLabel =
        receptorTemplate.type === RECEPTOR_TYPE.RECEPTOR
          ? receptorDisplayName
            ? receptorDisplayName.toLowerCase().endsWith(' receptor')
              ? receptorDisplayName
              : `${receptorDisplayName} Receptor`
            : 'Receptor'
          : getReceptorTypeLabel(receptorTemplate.type)

      const isRunning = inProgressScans
        ?.getScansList()
        .some((scan) => scan.getReceptorId() === r.getId())

      return {
        id: r.getId(),
        name: receptorName,
        type: receptorTypeLabel,
        receptor: r.getModelid(),
        isEnabled: r.getIsenabled(),
        connectedStatus: (() => {
          if (isRunning) return 'Running'
          if (!r.getIsenabled()) return 'Disabled'
          if (r.getExceptions()) return 'Error'
          if (!r.getCredentials()) return 'Missing Credentials'
          if (!r.getIscredvalid()) return 'Invalid Credentials'
          return 'Connected'
        })() as DocumentScanGridData['connectedStatus'],
        exceptionMessage: r.getExceptions(),
        servicesCount: r.getAutomatedcontrolsList().length,
        schedule: (() => {
          if (!r.getIsenabled()) return ''
          if (!scheduleId) return schedule
          const ids = scheduleId.split(',')
          const names = ids
            .map(
              (id, index) =>
                schedulesMap.get(id) || schedule.split(',')[index]?.trim(),
            )
            .filter(Boolean)
          return names.join(', ')
        })(),
        lastScan,
        lastScanStatus,
        visibilities: [],
        scheduleId,
        receptorId: r.getId(),
        isFolderScan: false,
      }
    })

    const filteredSettings =
      directoryScanSettings?.getSettingsList().filter((s) => {
        const settingReceptor = receptors
          ?.getReceptorsList()
          .find((r) => r.getId() === s.getReceptorId())
        if (
          settingReceptor &&
          !matchesStatusFilter(settingReceptor.getIsenabled())
        ) {
          return false
        }
        if (
          documentTypeFilter !== undefined &&
          s.getDocumentType() !== documentTypeFilter
        )
          return false

        if (!hasAnyFilter) return true

        const isSpecificMatch = folderScanIds.includes(s.getId())
        if (isSpecificMatch) return true

        return isGroupSelected('folder_scan')
      }) || []

    const mappedSettings =
      filteredSettings.map((s): DocumentScanGridData => {
        const requestReceptorId = s.getReceptorId()
        const receptor = receptors
          ?.getReceptorsList()
          .find((r) => r.getId() === requestReceptorId)

        const { schedule, lastScan, lastScanStatus, scheduleId } =
          getReceptorScheduleDetails(receptor)

        const isRunning = inProgressScans
          ?.getScansList()
          .some((scan) => scan.getReceptorId() === requestReceptorId)

        return {
          id: s.getId(),
          name: s.getName() || s.getRootPath(),
          type:
            s.getDocumentType() === DOCUMENT_TYPE.POLICYDOC
              ? 'Policy Folder Scan'
              : 'Evidence Folder Scan',
          receptor: receptor?.getModelid() || '',
          isEnabled: receptor?.getIsenabled() || false,
          connectedStatus: (() => {
            if (isRunning) return 'Running'
            if (receptor && !receptor.getIsenabled()) return 'Disabled'
            if (receptor && receptor.getExceptions()) return 'Error'
            if (receptor && !receptor.getCredentials())
              return 'Missing Credentials'
            if (receptor && !receptor.getIscredvalid())
              return 'Invalid Credentials'
            if (receptor) return 'Connected'
            return 'Error'
          })() as DocumentScanGridData['connectedStatus'],
          exceptionMessage: receptor ? receptor.getExceptions() : undefined,
          servicesCount: 0,
          schedule: (() => {
            if (!receptor?.getIsenabled()) return ''
            if (!scheduleId) return schedule
            const ids = scheduleId.split(',')
            const names = ids
              .map((id) => schedulesMap.get(id) || schedule.split(',')[0])
              .filter(Boolean)
            return names.join(', ')
          })(),
          lastScan,
          lastScanStatus,
          visibilities: [],
          scheduleId,
          receptorId: requestReceptorId,
          evidenceCount: s.getEvidenceCount ? s.getEvidenceCount() : 0,
          isFolderScan: true,
          documentType: s.getDocumentType(),
        }
      }) || []

    return [...mappedReceptors, ...mappedSettings]
  }, [
    receptors,
    directoryScanSettings,
    normalizedQueryParams,
    inProgressScans,
    schedulesMap,
    documentTypeFilter,
    showReceptors,
    receptorTypeFilter,
  ])

  useEffect(() => {
    setIsPolling(receptorsData.some((r) => r.connectedStatus === 'Running'))
  }, [receptorsData])

  const searchableReceptors = useMemo(
    () =>
      prepareSearchableData(
        receptorsData,
        (item: DocumentScanGridData) => item.id,
        (item: DocumentScanGridData) => [item.name, item.type, item.receptor],
      ),
    [receptorsData],
  )
  const { data: matchingReceptors } = useApplySearch(searchableReceptors)

  const pageSize = usePageSizeFromUrl(RECEPTORS_PAGE_SIZE)

  const sortedReceptors = useMemo(() => {
    return [...matchingReceptors].sort((a, b) => a.name.localeCompare(b.name))
  }, [matchingReceptors])

  const { paginatedItems, currentPage, nextPage, prevPage } =
    usePaginateItemsWithNavigate({
      items: sortedReceptors,
      pageSize,
    })

  const isDataLoaded =
    receptors !== undefined && directoryScanSettings !== undefined
  const hasAnyData = receptorsData.length > 0

  if (isDataLoaded && !hasAnyData) {
    return (
      <InformationalEmptyState
        title="No automated scans yet."
        note="Add a receptor or folder scan to automatically scan for documents."
      />
    )
  }

  if (isDataLoaded && hasAnyData && matchingReceptors.length === 0) {
    return <NoFilterResults />
  }

  return (
    <>
      <GridContainer>
        <StyledEvidenceGrid
          gridTemplateColumns={templateColumns}
          style={{ width: '100%' }}
        >
          <DocumentScanGridHeader />
          {paginatedItems.map((item) => (
            <DocumentScanGridRow
              key={item.id}
              {...item}
              schedulesMap={schedulesMap}
              openDropdownId={openDropdownId}
              onToggleDropdown={(isOpen: boolean, id: string) =>
                setOpenDropdownId(isOpen ? id : null)
              }
            />
          ))}
        </StyledEvidenceGrid>
      </GridContainer>
      {matchingReceptors.length > 0 && (
        <PaginateButtonsGroup
          totalItems={matchingReceptors.length}
          pageSize={pageSize}
          defaultPageSize={RECEPTORS_PAGE_SIZE}
          currentPage={currentPage}
          onPrevClick={prevPage}
          onNextClick={nextPage}
        />
      )}
    </>
  )
}

export const DocumentScanGridHeader = (): JSX.Element => {
  const showPolicy = useHasNewPolicyPage()
  return (
    <GridRow>
      <StickyGridHeader>Name</StickyGridHeader>
      <StickyGridHeader>Type</StickyGridHeader>
      <StickyGridHeader>Receptor Status</StickyGridHeader>
      <StickyGridHeader>Connection Status</StickyGridHeader>
      <StickyGridHeader>Evidence</StickyGridHeader>
      {showPolicy && <StickyGridHeader>Policy Documents</StickyGridHeader>}
      <StickyGridHeader>Schedule</StickyGridHeader>
      <StickyGridHeader>Last Scan</StickyGridHeader>
      <StickyGridHeader></StickyGridHeader>
    </GridRow>
  )
}

export const DocumentScanGridRow = (
  item: DocumentScanGridData & {
    schedulesMap: Map<string, string>
    openDropdownId: string | null
    onToggleDropdown: (isOpen: boolean, id: string) => void
  },
): JSX.Element => {
  const showPolicyDocumentsColumn = useHasNewPolicyPage()
  const { pageContext } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const isFromEvidence = location.pathname.includes('/evidence')
  const locationState = isFromEvidence
    ? { from: 'automated-scans' }
    : { from: 'receptors-index' }
  const existingSearch = queryString.parse(location.search || '')
  const permalink = linkWithSearch(
    `/${pageContext}/${ReceptorsAbsoluteRoutes.SHOW}/${item.id}`,
    {
      ...existingSearch,
      recordtype: item.isFolderScan ? 'folder' : 'receptor',
    },
  )
  return (
    <GridRow
      style={{ cursor: 'pointer' }}
      onClick={() => navigate(permalink, { state: locationState })}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          navigate(permalink, { state: locationState })
        }
      }}
    >
      <GridColumn>
        <FlexRow gap={8} align={FlexAlign.CENTER}>
          <ReceptorLogo receptorModelId={item.receptor} width="20px" />
          <div>{item.name}</div>
        </FlexRow>
      </GridColumn>
      <GridColumn>
        {!showPolicyDocumentsColumn && item.isFolderScan ? (
          'Folder Scan'
        ) : item.isFolderScan &&
          item.documentType === DOCUMENT_TYPE.POLICYDOC ? (
          <FlexRow gap={8} align={FlexAlign.CENTER}>
            <img
              src={PolicyIconPath}
              alt=""
              width={18}
              height={18}
              style={{ flexShrink: 0 }}
            />
            <span>{item.type}</span>
          </FlexRow>
        ) : item.isFolderScan &&
          item.documentType === DOCUMENT_TYPE.EVIDENCE ? (
          <FlexRow gap={8} align={FlexAlign.CENTER}>
            <img
              src={EvidenceIconPath}
              alt=""
              width={18}
              height={18}
              style={{ flexShrink: 0 }}
            />
            <span>{item.type}</span>
          </FlexRow>
        ) : (
          item.type
        )}
      </GridColumn>
      <GridColumn>{item.isEnabled ? 'Active' : 'Deactivated'}</GridColumn>
      <GridColumn>
        {item.connectedStatus === 'Error' && item.exceptionMessage ? (
          <PlainTooltip
            id={`tooltip-${item.id}`}
            placement={TooltipPositions.top}
            tooltipBody={item.exceptionMessage}
          >
            <FlexRow gap={8} align={FlexAlign.CENTER}>
              <StatusIcon status={item.connectedStatus} />
              <div>Error</div>
            </FlexRow>
          </PlainTooltip>
        ) : (
          <FlexRow gap={8} align={FlexAlign.CENTER}>
            <StatusIcon status={item.connectedStatus} />
            <div>
              {item.connectedStatus.charAt(0).toUpperCase() +
                item.connectedStatus.slice(1)}
            </div>
          </FlexRow>
        )}
      </GridColumn>
      <GridColumn>
        {item.isFolderScan ? (
          !showPolicyDocumentsColumn ? (
            <>{item.evidenceCount ?? 0}</>
          ) : item.documentType === DOCUMENT_TYPE.EVIDENCE ? (
            <>{item.evidenceCount ?? 0}</>
          ) : (
            <>0</>
          )
        ) : (
          <ReceptorEvidenceCount receptorId={item.receptorId} />
        )}
      </GridColumn>
      {showPolicyDocumentsColumn && (
        <GridColumn>
          {item.isFolderScan ? (
            item.documentType === DOCUMENT_TYPE.POLICYDOC ? (
              <>{item.evidenceCount ?? 0}</>
            ) : (
              <>0</>
            )
          ) : (
            <ReceptorPolicyDocCount receptorId={item.receptorId} />
          )}
        </GridColumn>
      )}
      <GridColumn>
        {item.isEnabled && (
          <div onClick={(e) => e.stopPropagation()}>
            {item.scheduleId?.split(',').map((id, index, array) => {
              const trimmedId = id.trim()
              const scheduleDisplay =
                item.schedulesMap.get(trimmedId) ||
                item.schedule.split(',')[index]?.trim() ||
                'Schedule'
              return (
                <React.Fragment key={trimmedId}>
                  <Link
                    to={`/${pageContext}/scheduling/show/${trimmedId}`}
                    style={{ textDecoration: 'underline', color: 'inherit' }}
                  >
                    {scheduleDisplay}
                  </Link>
                  {index < array.length - 1 && ', '}
                </React.Fragment>
              )
            })}
          </div>
        )}
      </GridColumn>
      <GridColumn>
        <FlexRow gap={8} align={FlexAlign.CENTER}>
          {item.isEnabled && item.lastScan && item.lastScan.length > 0 && (
            <>
              <StatusIcon status={item.lastScanStatus} />
              <div>{item.lastScan}</div>
            </>
          )}
        </FlexRow>
      </GridColumn>
      <GridColumn>
        <div
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
          }}
          style={{ pointerEvents: 'auto' }}
        >
          <DocumentScanGridRowMenu
            id={item.id}
            isFolderScan={item.isFolderScan}
            receptorId={item.receptorId}
            openDropdownId={item.openDropdownId}
            onToggleDropdown={item.onToggleDropdown}
            modelid={item.receptor}
            connectedStatus={item.connectedStatus}
          />
        </div>
      </GridColumn>
    </GridRow>
  )
}

export const DocumentScanGridRowMenu = ({
  id,
  isFolderScan,
  receptorId,
  openDropdownId,
  onToggleDropdown,
  modelid,
  connectedStatus,
}: {
  id: string
  isFolderScan: boolean
  receptorId: string
  openDropdownId: string | null
  onToggleDropdown: (isOpen: boolean, id: string) => void
  modelid: string
  connectedStatus: string
}): JSX.Element => {
  const deleteReceptor = useDeleteReceptor()
  const deleteDirectoryScan = useDeleteDirectoryScanSetting()
  const scanReceptor = useScanReceptor()
  const scanDirectorySetting = useScanDirectorySetting()
  const activateReceptor = useActivateReceptor()
  const title = isFolderScan ? 'Delete Folder Scan' : 'Delete Receptor'
  const body = isFolderScan
    ? 'Are you sure you want to delete this folder scan?'
    : 'Are you sure you want to delete this receptor?'

  const showDeleteConfirmation = useConfirmationModal({
    onConfirmCB: async () => {
      if (isFolderScan) {
        await deleteDirectoryScan(id, receptorId)
      } else {
        await deleteReceptor({ receptorId: id })
      }
    },
    title,
    body,
    confirmText: 'Delete',
    analyticsEvent: APP_ANALYTICS_EVENTS.RECEPTOR_DELETE,
  })

  const scanLabel = useMemo(() => {
    switch (modelid) {
      case 'trr-sharepoint':
      case 'trr-gdrive':
      case 'trr-confluence-dms':
        return 'Scan Now'
      default:
        return 'Collect Evidence'
    }
  }, [modelid])

  const runScan = async () => {
    if (connectedStatus !== 'Connected') return

    try {
      if (isFolderScan) {
        await scanDirectorySetting({ receptorId, scanSettingId: id })
      } else {
        await scanReceptor({
          receptorId,
          receptorModelId: modelid,
        })
      }
      showInfoToast('Evidence collection started.')
    } catch (err) {
      showInfoToast(
        'Sorry, there was an issue collecting your evidence. Please try again later.',
      )
      log.error('Failed to complete receptor scan: ', err)
    }
  }

  return (
    <Dropdown
      id={E2E_IDS.DOCUMENT_SCAN_ROW_MENU(id)}
      data-testid={E2E_IDS.DOCUMENT_SCAN_ROW_MENU(id)}
      show={openDropdownId === id}
      onToggle={(isOpen) => onToggleDropdown(isOpen, id)}
    >
      <Dropdown.Toggle as={DropdownToggleButton}>
        <VerticalEllipsesIcon />
      </Dropdown.Toggle>
      <BasicDropdownMenu>
        {connectedStatus === 'Disabled' && (
          <BasicDropdownMenuItem
            onClick={async () => {
              await activateReceptor({ receptorId })
            }}
          >
            Enable
          </BasicDropdownMenuItem>
        )}
        <BasicDropdownMenuItem
          onClick={runScan}
          disabled={connectedStatus !== 'Connected'}
        >
          {scanLabel}
        </BasicDropdownMenuItem>
        <BasicDropdownMenuItem onClick={showDeleteConfirmation}>
          Delete
        </BasicDropdownMenuItem>
      </BasicDropdownMenu>
    </Dropdown>
  )
}

const ReceptorEvidenceCount = ({
  receptorId,
}: {
  receptorId: string
}): JSX.Element => {
  const { data: scanSettingsData } = useListDirectoryScanSettings(receptorId)
  const req = useMemo(() => {
    const request = new GetEvidenceIndexPageRequest()
    request.setReceptorIdList([receptorId])
    return request
  }, [receptorId])

  const { data } = useEvidenceIndexPage(req)
  const count = useMemo(() => {
    const scanSettings = scanSettingsData?.getSettingsList() || []
    if (scanSettings.length > 0) {
      return scanSettings
        .filter(
          (setting) => setting.getDocumentType() === DOCUMENT_TYPE.EVIDENCE,
        )
        .reduce((sum, setting) => sum + setting.getEvidenceCount(), 0)
    }
    return data?.getItemsList().length ?? 0
  }, [data, scanSettingsData])

  return <>{count}</>
}

const ReceptorPolicyDocCount = ({
  receptorId,
}: {
  receptorId: string
}): JSX.Element => {
  const { data } = useListDirectoryScanSettings(receptorId)
  const count = useMemo(() => {
    if (!data) return 0
    return data
      .getSettingsList()
      .filter((s) => s.getDocumentType() === DOCUMENT_TYPE.POLICYDOC)
      .reduce(
        (sum, s) => sum + (s.getEvidenceCount ? s.getEvidenceCount() : 0),
        0,
      )
  }, [data])
  return <>{count}</>
}

export const StatusIcon = ({
  status,
}: {
  status: string
}): JSX.Element | null => {
  switch (status.toLocaleLowerCase()) {
    case 'success':
    case 'connected':
      return <SuccessIcon />
    case 'error':
    case 'missing credentials':
    case 'invalid credentials':
      return <ErrorIcon />
    case 'running':
      return <Spinner size="xs" color="primary" />
    case 'disabled':
      return null
    default:
      return null
  }
}

export const StatusIconWithText = ({
  status,
}: {
  status: string
}): JSX.Element => {
  const formattedStatus = status
    ? status.charAt(0).toUpperCase() + status.slice(1)
    : ''
  return (
    <FlexRow gap={8} align={FlexAlign.CENTER} justify={FlexAlign.FLEX_START}>
      <StatusIcon status={status} />
      <div>{formattedStatus}</div>
    </FlexRow>
  )
}
